type Point = [number, number];
type Triangle = [Point, Point, Point];
export class Sierpinski {
  private ctx: CanvasRenderingContext2D;
  boundingTriangle: Triangle;

  constructor(private canvas: HTMLCanvasElement) {
    const context = canvas.getContext("2d");
    if (!context) {
      throw new Error("Failed to get 2D context. Ensure your browser supports canvas.");
    }
    this.ctx = context;
    this.boundingTriangle = this.init();
  }

  /**
   * Draws a dot on the canvas at the specified location.
   * @param x - The x-coordinate of the dot.
   * @param y - The y-coordinate of the dot.
   * @param radius - The radius of the dot (optional, default is 5).
   * @param color - The color of the dot (optional, default is black).
   */
  drawDot(x: number, y: number, radius: number = 0.25, color: string = "black"): void {
    this.ctx.beginPath();
    this.ctx.arc(x, y, radius, 0, Math.PI * 2);
    this.ctx.fillStyle = color;
    this.ctx.fill();
    this.ctx.closePath();
  }

  do(p: Point, counter: number = 10000) {
    while (counter-- > 0) {
      const c = [p[0], p[1]];
      setTimeout(async () => {
        this.drawDot(c[0], c[1]);
        await delay(10);
      }, 1);
      const chosenPoint = pickRandom(this.boundingTriangle);
      p = [(p[0] + chosenPoint[0]) / 2, (p[1] + chosenPoint[1]) / 2];
    }
  }
  /**
   * Draws the largest equilateral triangle that can fit inside the canvas
   * by plotting its three corners.
   */
  init(): Triangle {
    const canvasWidth = this.canvas.width;
    const canvasHeight = this.canvas.height;

    console.log("width", canvasWidth, "height ", canvasHeight);
    // Calculate the height and width of the equilateral triangle
    const sideLength = Math.min(canvasWidth, canvasHeight);
    const height = (Math.sqrt(3) / 2) * sideLength; // Height of an equilateral triangle

    // Calculate the three points of the triangle
    const p1 = { x: canvasWidth / 2, y: (canvasHeight - height) / 2 }; // Top point
    const p2 = { x: (canvasWidth - sideLength) / 2, y: (canvasHeight + height) / 2 }; // Bottom-left point
    const p3 = { x: (canvasWidth + sideLength) / 2, y: (canvasHeight + height) / 2 }; // Bottom-right point

    // Draw the three points of the equilateral triangle
    this.drawDot(p1.x, p1.y); // Top point
    this.drawDot(p2.x, p2.y); // Bottom-left point
    this.drawDot(p3.x, p3.y); // Bottom-right point
    return [
      [p1.x, p1.y],
      [p2.x, p2.y],
      [p3.x, p3.y],
    ];
  }
  public PointInTriangle(p: Point): boolean {
    return pointInTriangle(p, this.boundingTriangle);
  }
}

export function pointInTriangle(p: Point, t: Triangle): boolean {
  const [p1, p2, p3] = t;

  // Function to compute the signed area of a triangle (with respect to the origin)
  const area = (a: Point, b: Point, c: Point): number => {
    return a[0] * (b[1] - c[1]) + b[0] * (c[1] - a[1]) + c[0] * (a[1] - b[1]);
  };

  // Area of the whole triangle
  const A = area(p1, p2, p3);

  // Area of the three sub-triangles formed with point p
  const A1 = area(p, p2, p3);
  const A2 = area(p1, p, p3);
  const A3 = area(p1, p2, p);

  // Point is inside the triangle if the sum of the areas of the sub-triangles is the same as the area of the triangle
  return A > 0 ? A1 > 0 && A2 > 0 && A3 > 0 : A1 < 0 && A2 < 0 && A3 < 0;
}
function pickRandom<T>(arr: T[]): T {
  const randomIndex = Math.floor(Math.random() * arr.length);
  return arr[randomIndex];
}
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
